/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import { Alert } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';
import { NotificationType } from '../../models/notification-message-type';
import { INotificationData } from './notification.data';
import { INotificationProps } from './notification.props.autogenerated';

/**
 *
 * Notification component
 * @extends {React.Component<INotificationProps<INotificationData>>}
 */
class Notification extends React.Component<INotificationProps<INotificationData>> {
    public render(): JSX.Element | null {
        const moduleData = this.props.data;
        if (!(moduleData && moduleData.notification && moduleData.notification.result)) {
            return null;
        }
        const notificationMessageObject = moduleData.notification.result;
        if (notificationMessageObject.message && !notificationMessageObject.displayed) {
            return (
                <div className='message' onAnimationEnd={this._markNotificationDisplayed}>
                    {this._renderErrorBlock(notificationMessageObject.message, notificationMessageObject.messagetype)}
                </div>
            );
        }
        return null;
    }

    public shouldComponentUpdate(nextProps: INotificationProps<INotificationData>): boolean {
        return Boolean(nextProps.data.notification && nextProps.data.notification.status === 'SUCCESS');
    }

    private _markNotificationDisplayed = () => {
        this.props.data.notification!.result!.displayed = true;
        this.forceUpdate();
    };

    private _renderErrorBlock = (message: string, _errorType: NotificationType): JSX.Element | null => {
        const alertcolorclassName = this._getClassName(_errorType,'color');
        const messageTextClassName = this._getClassName(_errorType,'text');
        return (
            <Alert className='message-alert' color={alertcolorclassName}>
                <div className={messageTextClassName}>{message}</div>
            </Alert>
        );
    };

    private _getClassName = (errorType: NotificationType ,classtype:string): string => {
        switch (errorType) {
            case NotificationType.error:
                return `message-error-${classtype}`;
            case NotificationType.info:
                return `message-info-${classtype}`;
            case NotificationType.success:
                return `message-success-${classtype}`;
            case NotificationType.validation:
                return `message-validation-${classtype}`;
            case NotificationType.warning:
                return `message-warning-${classtype}`;
            case NotificationType.none:
                return '';
            default:
                return '';
        }
    };
}

export default Notification;
